import { Modal, Box, Typography, IconButton, List, ListItem, ListItemText } from '@mui/material';
import { Close } from '@mui/icons-material';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', md: 800 },
    maxHeight: '90vh',
    overflowY: 'auto',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
};

export default function QuickReferenceModal({ open, onClose }) {
    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={modalStyle}>
                <IconButton
                    onClick={onClose}
                    sx={{ position: 'absolute', top: 8, right: 8 }}
                >
                    <Close />
                </IconButton>

                <Typography variant="h5" gutterBottom fontWeight="bold">
                    RBS Co-Pilot Quick Reference Guide
                </Typography>

                <Typography variant="body1" paragraph>
                    The RBS CoPilot helps you quickly find accurate information from our knowledge base to assist callers.
                </Typography>

                {/* Getting the Best Results */}
                <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mt: 3 }}>
                    🎯 Getting the Best Results
                </Typography>

                <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>
                    1. Provide Context First
                </Typography>
                <List dense sx={{ pl: 2 }}>
                    <ListItem><ListItemText primary="Active employee, retiree, or beneficiary?" /></ListItem>
                    <ListItem><ListItemText primary="Which division or plan? (if relevant)" /></ListItem>
                    <ListItem><ListItemText primary="Any special circumstances?" /></ListItem>
                </List>
                <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', pl: 2 }}
                >
                    Example: "For an active TELUS employee, what is the process to add a spouse to health benefits?"
                </Typography>

                <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
                    2. Use Clear, Simple Language
                </Typography>
                <List dense sx={{ pl: 2 }}>
                    <ListItem><ListItemText primary="Write complete questions, not just keywords" /></ListItem>
                    <ListItem><ListItemText primary="Avoid jargon or acronyms unless common" /></ListItem>
                    <ListItem><ListItemText primary="End questions with a question mark (?)" /></ListItem>
                </List>

                <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
                    3. Choose Your Approach: Questions vs. Commands
                </Typography>

                <Typography variant="body2" sx={{ mt: 1, pl: 2 }}>
                    <strong>📋 Use QUESTIONS</strong> when you need explanation:
                </Typography>
                <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', pl: 3 }}
                >
                    "What is the process to report the passing of a retiree?"
                </Typography>
                <Typography variant="body2" sx={{ pl: 3 }}>
                    Result: Detailed explanation with context and background
                </Typography>

                <Typography variant="body2" sx={{ mt: 2, pl: 2 }}>
                    <strong>⚡ Use COMMANDS</strong> when you need quick facts:
                </Typography>
                <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', pl: 3 }}
                >
                    "Provide the process to report the passing of a retiree"
                </Typography>
                <Typography variant="body2" sx={{ pl: 3 }}>
                    Result: Focused, step-by-step instructions
                </Typography>

                {/* Quick Command Examples */}
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }} fontWeight="bold">
                    🚀 Quick Command Examples
                </Typography>
                <List dense sx={{ pl: 2 }}>
                    <ListItem><ListItemText primary="Provide the phone number for..." /></ListItem>
                    <ListItem><ListItemText primary="List the steps to..." /></ListItem>
                    <ListItem><ListItemText primary="Show the requirements for..." /></ListItem>
                    <ListItem><ListItemText primary="Give me the address for..." /></ListItem>
                </List>

                {/* Pro Tips */}
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }} fontWeight="bold">
                    💡 Pro Tips
                </Typography>
                <List dense sx={{ pl: 2 }}>
                    <ListItem><ListItemText primary="Be complete: Include all necessary context in your single query" /></ListItem>
                    <ListItem><ListItemText primary="Be specific: Instead of 'benefits,' say 'health benefits' or 'pension benefits'" /></ListItem>
                </List>

                {/* Help Us Improve */}
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }} fontWeight="bold">
                    👎 Help Us Improve
                </Typography>
                <Typography variant="body2" paragraph sx={{ pl: 2 }}>
                    If the response wasn't helpful, use the thumbs down button!
                </Typography>
                <Typography variant="body2" paragraph sx={{ pl: 2 }}>
                    Your feedback helps us:
                </Typography>
                <List dense sx={{ pl: 2 }}>
                    <ListItem><ListItemText primary="Identify gaps in our knowledge base" /></ListItem>
                    <ListItem><ListItemText primary="Update outdated information" /></ListItem>
                    <ListItem><ListItemText primary="Improve response accuracy" /></ListItem>
                </List>
                <Typography variant="body2" sx={{ pl: 2 }}>
                    We review all feedback and update the knowledge base as needed to ensure the best answers are provided.
                </Typography>
            </Box>
        </Modal>
    );
}
